import { useState, useEffect, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import { Minimize2 } from "lucide-react";
import ChatService from "../../front-services/ChatService";
import ChatCache from "../../utils/ChatCache";
import { ChatHeader } from "./shared/ChatHeader";
import { ChatMessages } from "./shared/ChatMessages";
import { ChatFooter } from "./shared/ChatFooter";
import { VoicePanel } from "./voice/components/VoicePanel";
import { ChatButton, clearUnreadBadge } from "./shared/ChatButton";
import { Slider } from "../../widgets/Chat/components/slider/Slider";
import WideImageViewer from "./components/WideImageViewer";
import HandoffForm from "./components/Handoff/HandoffForm";
import constants from "../../utils/Constants";
import Analytics from "../../utils/Analytics";
import Utility from "./logic/Utility";
import MessageHandler from "./logic/MessageHandler";
import { useSliderState } from "./components/slider/SliderState";
import { isExpandableTemplate } from "./components/slider/templates/utils/showcase";
import localization, { SUPPORTED_LANGUAGES } from "../../assets/localization";
import getInternalStyle from "../../utils/Styles";
import Colors from "../../utils/Colors";
import Modes from "../../utils/Modes";
import { getVariant, setVariantMode } from "../../utils/Experiment";
import { markWidgetOpened } from "../../utils/WidgetOpenState";
import {
  resolveLauncherPosition,
  resolvePanelAnchor,
} from "./shared/launcherPosition";
import { useBookingState } from "./components/slider/booking/BookingState";
import HostApi, {
  OpenOptions,
  SendMessageOptions,
  resolveOrigin,
} from "../../utils/HostApi";

const chatService = new ChatService();
const messageHandler = new MessageHandler();
const chatCache = new ChatCache();

interface ChatProps {
  startOpen?: boolean;
  isMobile?: boolean;
  isEditor?: boolean;
  editorStyle?: any;
  assistant: any;
  /**
   * The locale the caller named: the host page's on an embedded widget, the
   * URL's on the dedicated page. Undefined only on the dedicated page opened
   * without a `?lang=`, where the assistant's own configuration answers.
   */
  language?: string;
  mode?: string;
}

interface AssistantChatConfiguration {
  loaded: boolean;
  isStreamEnabled?: boolean;
  isVoiceModeEnabled?: boolean;
  // "Continue on WhatsApp" beside the handoff button. Needs phoneNumber too:
  // the flag says the hotel wants it, the number is what makes it clickable.
  isWhatsappHandoffEnabled?: boolean;
  phoneNumber?: string;
  // The hotel's voice line for the "call us" card in the welcome message.
  // Present only when the button is on and the line is enabled.
  voiceCallNumber?: string | null;
  // Open-rate experiment surface (backoffice-configurable, all optional —
  // the widget has localized defaults for each):
  widgetVariant?: string; // "A" | "B" | "split"
  teaserPillFixed?: string;
  teaserPillsRotating?: string[];
  autoOpenEnabled?: boolean;
  autoOpenDelaySeconds?: number;
}

export const Chat = ({
  mode = "0",
  isMobile = false,
  isEditor = false,
  editorStyle = null,
  assistant,
  language: requestedLanguage,
}: ChatProps) => {
  // Standalone: the widget IS the page — always open, never a launcher.
  const isStandalone = Modes.isStandalone(mode);
  const showsLauncher = !Modes.hidesLauncher(mode);

  // The dedicated page has no host site to read a language off, so when its URL
  // names none the assistant's configuration decides and we adopt the locale the
  // backend says it served. Deliberately NOT a dependency of the configuration
  // fetch below: adopting it must not fire a second request.
  const asksConfigurationForLanguage = isStandalone && !requestedLanguage;
  const [servedLanguage, setServedLanguage] = useState<string | null>(null);
  const language = servedLanguage ?? requestedLanguage ?? "en";

  const [isOpen, setIsOpen] = useState(
    isEditor ||
      isStandalone ||
      mode === Modes.OPEN_BY_DEFAULT_AND_DESAPPEARES_IF_CLOSED
  );
  // Viewport point the opening animation grows from, when the host site told
  // us which element the guest clicked. Cleared once the animation is done.
  const [openOrigin, setOpenOrigin] = useState<{ x: number; y: number } | null>(
    null
  );
  const [messages, setMessages] = useState<any[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isMessageLoading, setIsMessageLoading] = useState(false);
  const [isMessageCompleted, setIsMessageCompleted] = useState(true);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [toolName, setToolName] = useState<string | null>(null);
  const [welcomeMessageTime] = useState(Utility.getTime());
  const [showHandoffButton, setShowHandoffButton] = useState(true);
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  // AI Act Art. 50(1): the disclosure has to land before the guest's first
  // interaction, not before their first-ever one. Scoped to the session rather
  // than to `messages` (which comes back from cache) so a returning guest is
  // told again instead of silently skipping it.
  const [hasSentMessageThisSession, setHasSentMessageThisSession] =
    useState(false);
  const sliderState = useSliderState();
  const [mountedOpen, setMountedOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setMountedOpen(true);
    }, 250);
    return () => clearTimeout(timer);
  }, []);

  const [fullScreen, setFullScreen] = useState({
    isWIVFullScreen: false,
    WIVfullScreenImage: null,
    isHandoffFormFullScreen: false,
  });

  const [ready, setReady] = useState(false);
  const [style, setStyle] = useState({
    color: editorStyle?.mainColor || "#a72eff",
    name: "Nome Assistente",
    fileUrl: "https://plutonios.com/SmallCircle.webp",
    loaded: false,
    ...editorStyle,
  });
  const [assistantChatConfiguration, setAssistantChatConfiguration] =
    useState<AssistantChatConfiguration>({
      loaded: false,
    });

  const chatMessagesRef = useRef<HTMLDivElement | null>(null);
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Leaving fullscreen: AnimatePresence keeps the exiting copy of the chat
  // mounted through its exit animation, AFTER the floating copy has already
  // re-attached these shared refs. Its late unmount would null them out and
  // kill auto-scroll for good, so attach through callbacks that ignore
  // detaches — whichever copy mounts last owns the ref.
  const attachChatMessages = (node: HTMLDivElement | null) => {
    if (node) chatMessagesRef.current = node;
  };
  const attachLastMessage = (node: HTMLDivElement | null) => {
    if (node) lastMessageRef.current = node;
  };

  // The handoff form is an animated overlay now — the chat stays mounted
  // (and visible, blurred) behind it, so it no longer counts as fullscreen.
  const isAnyFullScreen = fullScreen.isWIVFullScreen;

  // In fullscreen the conversation moves INSIDE the immersive surface as its
  // own column; the floating widget disappears entirely until it closes.
  const isImmersiveChat =
    sliderState.isExpanded &&
    !isMobile &&
    isExpandableTemplate(sliderState.currentTemplate);

  const scrollToBottom = () => {
    if (!chatMessagesRef.current || messages.length === 0) return;

    const chatContainer = chatMessagesRef.current;
    const lastMessage = messages[messages.length - 1];

    if (lastMessageRef.current && lastMessage?.templates) {
      const offset = -100;
      chatContainer.scrollTo({
        top: lastMessageRef.current.offsetTop + offset,
        behavior: "smooth",
      });
    } else {
      chatContainer.scrollTo({
        top: chatContainer.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  // The conversation comes back from the cache once per assistant. Not on a
  // language change: the host site can switch language mid-chat
  // (PlutoniosChat.setLanguage, or `<html lang>` when the embed follows it), and
  // re-reading the cache then would drop a reply that is still streaming.
  useEffect(() => {
    if (!assistant.id) return;
    const cachedMessages = chatCache.getMessages(assistant.id);
    const cachedThreadId = chatCache.getThreadId(assistant.id);
    setMessages(cachedMessages);
    setThreadId(cachedThreadId);
    sliderState.hydrateFromCache(
      cachedThreadId,
      assistant.id,
      cachedMessages
        .filter((message: any) => message.sliderTemplates)
        .map((message: any) => message.sliderTemplates),
      isMobile
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistant]);

  useEffect(() => {
    sliderState.setLanguage(language);
    if (assistant.id) {
      Promise.all([
        chatService
          .getAssistantChatConfiguration(
            assistant.id,
            requestedLanguage,
            isStandalone ? Modes.STANDALONE : undefined
          )
          .then((response) => {
            if (response.ok) return response.json();
          }),
        chatService.getWidgetConfiguration(assistant.id).then((response) => {
          if (response.ok) return response.json();
        }),
      ]).then(([configData, widgetData]) => {
        if (configData) {
          // The language the dedicated page renders its own labels in, when the
          // URL named none. Applied here, in the same batch as the style below:
          // nothing paints before `style.loaded`, so the guest never sees the
          // English chrome flip to the configured language.
          if (
            asksConfigurationForLanguage &&
            SUPPORTED_LANGUAGES.includes(configData.servedLocale)
          ) {
            setServedLanguage(configData.servedLocale);
            sliderState.setLanguage(configData.servedLocale);
          }
          // Resolve the experiment arm BEFORE anything renders or tracks:
          // the hotel can force A/B/split from the backoffice; default is "A"
          // (classic) for now — the proactive arm ships only where explicitly
          // configured.
          setVariantMode(configData.widgetVariant ?? "A");
          // Already the AND of the integration capability and the
          // per-assistant toggle — the widget stores the verdict, it does not
          // recompute it.
          useBookingState
            .getState()
            .setEnabled(configData.isBookingEnabled === true);
          setAssistantChatConfiguration({ isVoiceModeEnabled: false, ...configData, loaded: true });
        }
        // Assistants can exist without a widget configuration (used to crash
        // on null.style): fall back to default branding — except in editor
        // mode, where the backoffice-provided editorStyle must survive.
        if (widgetData && !(isEditor && !widgetData.configuration?.style)) {
          const resStyle = widgetData.configuration?.style ?? {};
          Colors.applyBrandTokens(resStyle.mainColor || "#a72eff");
          const _style = {
            color: resStyle.mainColor || "#a72eff",
            name: resStyle.name || "Nome Assistente",
            fileUrl:
              resStyle.fileUrl || "https://plutonios.com/SmallCircle.webp",
            loaded: true,
            bot_icon: resStyle.bot_icon || null,
            open_button_image: resStyle.open_button_image || null,
            position: resStyle.position || null,
          };
          document.documentElement.style.setProperty(
            "--text-color",
            getInternalStyle(assistant.id, _style).text.color
          );
          setStyle(_style);
        }
      });
    }
    // `requestedLanguage`, not the resolved `language`: the locale we ADOPT from
    // the response must not re-run the request that produced it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistant, requestedLanguage]);

  // Editor mode boots from editorStyle before (or instead of) the fetched
  // configuration — the brand CSS vars must exist from the first paint.
  useEffect(() => {
    if (isEditor) {
      Colors.applyBrandTokens(editorStyle?.mainColor || "#a72eff");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleWidgetMessage = (event: any) => {
      const message = event.detail;

      if (message.color) {
        setStyle(message);
        Colors.applyBrandTokens(message.color);
      }

      // Backoffice live preview: unsaved chat-config edits are pushed here so
      // the editor widget reflects them without a save round-trip. The sender
      // omits empty fields so the widget's locale defaults keep working.
      if (message.chatConfig) {
        if (message.chatConfig.widgetVariant) {
          setVariantMode(message.chatConfig.widgetVariant);
        }
        setAssistantChatConfiguration((prev: AssistantChatConfiguration) => ({
          ...prev,
          ...message.chatConfig,
          loaded: true,
        }));
      }

      // Closed/open toggle of the preview frame.
      if (message.editorPreview) {
        setIsOpen(!!message.editorPreview.open);
      }
    };

    const shadowHost = document.getElementById("widget-editor");
    if (shadowHost && shadowHost.shadowRoot) {
      shadowHost.shadowRoot.addEventListener(
        "widgetMessage",
        handleWidgetMessage
      );
    }

    // Dev-only: standalone mode has no backoffice shadow host, so the same
    // payload can also arrive on window. It lets a local demo wear a real
    // customer's branding without pointing the widget at prod — which would
    // otherwise post widget_impression events into that customer's live
    // funnel, the very baseline we measure against.
    const isDev = process.env.NODE_ENV === "development";
    if (isDev) window.addEventListener("widgetMessage", handleWidgetMessage);

    return () => {
      if (shadowHost && shadowHost.shadowRoot) {
        shadowHost.shadowRoot.removeEventListener(
          "widgetMessage",
          handleWidgetMessage
        );
      }
      if (isDev) window.removeEventListener("widgetMessage", handleWidgetMessage);
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, isMessageLoading]);

  // Entering/leaving fullscreen remounts the message list, and closing the
  // widget collapses the scroll container to height 0 (losing its position).
  // In both cases jump (not smooth-scroll) to the latest messages, with
  // retries because the container is still transitioning/mounting.
  useEffect(() => {
    const jumpToBottom = () => {
      const container = chatMessagesRef.current;
      if (container) container.scrollTop = container.scrollHeight;
    };
    const timers = [60, 250, 550].map((ms) => setTimeout(jumpToBottom, ms));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sliderState.isExpanded, isOpen]);

  // The image viewer and the handoff form live in the widget container: if
  // one opens while in fullscreen, come back to the widget so it's visible.
  useEffect(() => {
    if (fullScreen.isWIVFullScreen || fullScreen.isHandoffFormFullScreen) {
      sliderState.setExpanded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullScreen.isWIVFullScreen, fullScreen.isHandoffFormFullScreen]);

  useEffect(() => {
    chatService.ping(assistant.id).then((response) => {
      if (response.ok) setReady(true);
    });
  }, [assistant]);

  // Behavioral analytics: one impression per page load once the button is
  // actually usable (ready) AND the configuration has resolved the experiment
  // arm — otherwise a forced-variant hotel would log the impression with the
  // split-derived variant. The ref guards the double-fire when ready and
  // loaded flip in separate renders.
  const impressionTracked = useRef(false);
  useEffect(() => {
    if (!ready || !assistantChatConfiguration.loaded) return;
    if (impressionTracked.current) return;
    impressionTracked.current = true;
    Analytics.init({ assistantId: assistant.id, language, isEditor });
    Analytics.track("widget_impression", {
      mode,
      // Loaded through public/legacy/main-shim.js: the page embeds a bundle
      // name that is no longer published. Lets us find those sites in
      // widget_events before the shim is the only thing keeping them up.
      ...(window.__PLUTONIOS_LEGACY_EMBED__ ? { legacy_embed: true } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, assistantChatConfiguration.loaded]);

  // Any manual open/close disarms the pending auto-open.
  const userInteracted = useRef(false);

  // Variant B auto-open: desktop only, ONE page load per visitor ever (the
  // very first), configurable delay, and it stands down if the guest beats
  // it to any interaction. Research note: full auto-open is Baymard's #1
  // chat anti-pattern on mobile — hence desktop-only and once-ever.
  const FIRST_VISIT_KEY = "drastik_widget_visited";
  useEffect(() => {
    if (!ready || !assistantChatConfiguration.loaded) return;
    if (isEditor || isMobile || isOpen) return;
    if (mode !== "0") return; // never fight a host-forced mode
    if (getVariant() !== "B") return;
    if (assistantChatConfiguration.autoOpenEnabled === false) return;

    let firstVisit = false;
    try {
      firstVisit = !localStorage.getItem(FIRST_VISIT_KEY);
      // Marked immediately: "first visit" means the first page load, not
      // the first N loads until the timer manages to fire.
      localStorage.setItem(FIRST_VISIT_KEY, "1");
    } catch {
      firstVisit = false;
    }
    if (!firstVisit) return;

    const delaySeconds = assistantChatConfiguration.autoOpenDelaySeconds ?? 6;
    const timer = setTimeout(() => {
      // Re-check at fire time: the guest may have opened (and even closed)
      // the widget in the meantime — an auto-reopen would be hostile.
      setIsOpen((prev) => {
        if (prev || userInteracted.current) return prev;
        bringHostToFront();
        clearUnreadBadge();
        markWidgetOpened();
        Analytics.track("widget_open", { source: "auto" });
        return true;
      });
    }, Math.max(2, delaySeconds) * 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, assistantChatConfiguration.loaded]);

  // Some host sites have a sticky bottom bar (e.g. a booking bar) that also
  // uses the maximum z-index. z-index alone can't beat an equal-z sibling that
  // sits later in the DOM, so we move our host to the end of <body> to win the
  // tie. No-op in editor/test mode where the host id doesn't match.
  const bringHostToFront = () => {
    if (isEditor) return;
    const host = document.getElementById(
      `plutonios-chat-widget-host-${assistant.id}`
    );
    const parent = host?.parentElement;
    if (host && parent && parent.lastElementChild !== host) {
      parent.appendChild(host);
    }
  };

  // A one-shot re-append loses to bars that (re)insert themselves after us on
  // load/scroll. Since both sit at the max z-index, DOM order is the only
  // tie-breaker, so we hold last position: re-append whenever a <body> child is
  // added/moved. Our own move sets us last, so the guard makes the next
  // callback a no-op — no loop unless the bar also fights for last (it doesn't).
  useEffect(() => {
    if (isEditor) return;
    bringHostToFront();
    const observer = new MutationObserver(() => bringHostToFront());
    observer.observe(document.body, { childList: true });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // source tells the funnel WHERE the open came from (launcher / teaser /
  // voice). Guarded because DOM handlers pass the click event as first arg.
  const toggleChatWidget = (source?: any) => {
    const label = typeof source === "string" ? source : "launcher";
    if (isOpen) closeWidget(label);
    else openWidget(label);
  };

  // The host site can tell us which element the guest clicked; the widget then
  // grows out of that point instead of the corner, so the connection between
  // "I clicked this card" and "this panel appeared" is never in doubt.
  const originTimer = useRef<number | null>(null);
  const playOriginAnimation = (origin: OpenOptions["origin"]) => {
    const point = resolveOrigin(origin);
    if (!point) return;

    setOpenOrigin(point);
    if (originTimer.current) window.clearTimeout(originTimer.current);
    // Cleared after the keyframe so the panel goes back to being plainly
    // positioned — a lingering animation would replay on the next re-render.
    originTimer.current = window.setTimeout(() => setOpenOrigin(null), 720);
  };

  useEffect(
    () => () => {
      if (originTimer.current) window.clearTimeout(originTimer.current);
    },
    []
  );

  const openWidget = (source: string, options?: OpenOptions) => {
    userInteracted.current = true;
    if (isOpen) return;

    playOriginAnimation(options?.origin);
    bringHostToFront();
    clearUnreadBadge();
    markWidgetOpened();
    Analytics.track("widget_open", { source });
    setIsOpen(true);
    HostApi.emit("open", { source });
  };

  const closeWidget = (source: string) => {
    userInteracted.current = true;
    // Standalone has no "closed" state to fall back to: the page would just go
    // blank, so the host API's close() is a deliberate no-op there.
    if (!isOpen || isStandalone) return;

    Analytics.track("widget_close", { source });
    sliderState.closeSlider();
    shutdownIfEnforced();
    setIsOpen(false);
    HostApi.emit("close", { source });
  };

  // A message the host site asked us to send on the guest's behalf (their
  // "discover more" button). It is a real guest turn — same thread, same
  // analytics funnel — flagged only so the bubble can type itself in.
  const sendHostMessage = async (
    text: string,
    options?: SendMessageOptions
  ) => {
    if (isEditor) return;

    if (options?.open !== false && !isOpen) {
      openWidget("host_api", options);
    } else {
      playOriginAnimation(options?.origin);
    }

    HostApi.emit("message", { text, source: "host_api" });
    await sendProgrammaticMessage(text, "host_api", { injected: true });
  };

  // Registered once, but always current: the handlers read through a ref that
  // every render refreshes, so a queued host call replayed later still sees
  // today's threadId and configuration rather than the ones from mount time.
  const hostHandlersRef = useRef({
    open: openWidget,
    close: closeWidget,
    sendMessage: sendHostMessage,
    isOpen,
  });
  hostHandlersRef.current = {
    open: openWidget,
    close: closeWidget,
    sendMessage: sendHostMessage,
    isOpen,
  };

  useEffect(() => {
    // The backoffice preview shares this bundle; it must not seize the API a
    // real embedded widget on the same page would be answering.
    if (isEditor) return;

    HostApi.connect({
      open: (options?: OpenOptions) =>
        hostHandlersRef.current.open("host_api", options),
      close: () => hostHandlersRef.current.close("host_api"),
      toggle: (options?: OpenOptions) =>
        hostHandlersRef.current.isOpen
          ? hostHandlersRef.current.close("host_api")
          : hostHandlersRef.current.open("host_api", options),
      sendMessage: (text: string, options?: SendMessageOptions) => {
        void hostHandlersRef.current.sendMessage(text, options);
      },
      isOpen: () => hostHandlersRef.current.isOpen,
    });

    return () => HostApi.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Variant B teaser pill: opening and asking are one gesture — open the
  // widget and immediately send the pill's question as the guest message.
  const handleTeaserClick = async (question: string) => {
    // Preview mode: show the gesture (chat opens) but never create real
    // threads/messages from the backoffice.
    if (isEditor) {
      setIsOpen(true);
      return;
    }
    if (!isOpen) toggleChatWidget("teaser");
    await sendProgrammaticMessage(question, "teaser");
  };

  const handleUserMessage = async () => {
    if (!inputMessage.trim()) return;

    initiateMessageState();

    const userMessage = {
      type: "user",
      content: inputMessage.trim(),
      time: Utility.getTime(),
    };
    setMessages((prev) => [...prev, userMessage]);
    chatCache.addMessage(userMessage, threadId, assistant.id);
    setInputMessage("");

    Analytics.track("message_sent", {
      source: "typed",
      length: userMessage.content.length,
    });

    const handler = assistantChatConfiguration.isStreamEnabled
      ? messageHandler.handleUserMessageWithStream
      : messageHandler.handleUserMessageWithoutStream;

    await handler(
      inputMessage.trim(),
      (newMessages: any) => {
        setMessages(newMessages);
        scrollToBottom();
      },
      setIsMessageLoading,
      setIsMessageCompleted,
      setThreadId,
      assistant,
      threadId,
      setToolName,
      language,
      isMobile
    );
  };

  const initiateMessageState = () => {
    setIsMessageCompleted(false);
    setIsMessageLoading(true);
    setHasSentMessageThisSession(true);
  };

  // The typed path is guarded by the send button's `disabled` (ChatFooter); the
  // shortcuts, the teaser pills and the quote pills had nothing. They are never
  // unmounted either, so they stay clickable for the whole turn — and a second
  // click before the server's `run.started` frame lands still sees threadId ===
  // null, so dengine mints a NEW thread id and the backend opens a SECOND
  // conversation. 18 of the 87 dead prod threads are exactly that: a pair with
  // the identical pill text, seconds apart.
  //
  // A ref, not `isMessageCompleted`: the state value read inside these handlers
  // is the one from the render that created them, so two clicks in the same tick
  // would both read `true` and both pass.
  const sendInFlight = useRef(false);

  const handleShortcutClick = (userMessage: string) => {
    if (isEditor) return; // preview only — no real messages from the backoffice
    return sendProgrammaticMessage(userMessage, "shortcut");
  };

  // Its own source, not "shortcut": the whole point of the pills is to be
  // measured against the shortcuts and the teaser in the same funnel, and a
  // shared label would make that impossible after the fact.
  const handleQuotePillClick = (userMessage: string) => {
    if (isEditor) return;
    return sendProgrammaticMessage(userMessage, "quote_pill");
  };

  // Shared path for messages the guest picks rather than types (welcome
  // shortcuts, variant-B teaser pills, host-injected questions); source feeds
  // the analytics funnel.
  const sendProgrammaticMessage = async (
    userMessage: string,
    source: string,
    options?: { injected?: boolean }
  ) => {
    if (sendInFlight.current) return;
    sendInFlight.current = true;
    initiateMessageState();

    const newMessage = {
      type: "user",
      content: userMessage,
      time: Utility.getTime(),
    };
    setMessages((prev) => [
      ...prev,
      // The type-in is a one-time entrance, so the flag stays out of the cached
      // copy — otherwise every reload would replay it on an old message.
      options?.injected ? { ...newMessage, injected: true } : newMessage,
    ]);
    chatCache.addMessage(newMessage, threadId, assistant.id);
    setInputMessage("");

    Analytics.track("message_sent", { source, text: userMessage });

    const handler = assistantChatConfiguration.isStreamEnabled
      ? messageHandler.handleUserMessageWithStream
      : messageHandler.handleUserMessageWithoutStream;

    try {
      await handler(
        userMessage,
        (newMessages: any) => {
          setMessages(newMessages);
          scrollToBottom();
        },
        setIsMessageLoading,
        setIsMessageCompleted,
        setThreadId,
        assistant,
        threadId,
        setToolName,
        language,
        isMobile
      );
    } finally {
      // Released whatever happened: a turn that threw must not leave the pills
      // dead for the rest of the session.
      sendInFlight.current = false;
    }
  };

  const cleanCache = () => {
    Analytics.track("chat_cleared");
    setMessages([]);
    setThreadId(null);
    chatCache.remove(assistant.id);
    sliderState.reset();
  };

  const shutdownIfEnforced = () => {
    const id = document.getElementById(
      `plutonios-chat-widget-host-${assistant.id}`
    );
    if (id && mode === Modes.OPEN_BY_DEFAULT_AND_DESAPPEARES_IF_CLOSED) {
      setTimeout(() => {
        id.remove();
      }, 300);
    }
  };

  // Launcher corner from the backoffice; the open desktop panel follows it
  // (same side, moved by the same amount). On a phone the panel is fullscreen.
  const launcher = resolveLauncherPosition(style.position, isMobile);
  const panelAnchor = resolvePanelAnchor(launcher);
  const isLeft = launcher.side === "left" && !isEditor;
  // A raised panel must still fit a short laptop screen.
  const raisedPanel = !isMobile && panelAnchor.bottom > 20;
  const openHeight = isMobile
    ? constants.DIMENSIONS.MOBILE.OPEN.HEIGHT_STRING
    : raisedPanel
    ? `min(${constants.DIMENSIONS.DESKTOP.OPEN.HEIGHT_STRING}, calc(100vh - ${
        panelAnchor.bottom + 20
      }px))`
    : constants.DIMENSIONS.DESKTOP.OPEN.HEIGHT_STRING;

  // Three surfaces, one component: the embedded panel pinned to a corner, the
  // standalone page filling a phone, and the standalone card centered on a
  // desktop backdrop (the template slider docks to its left via calc()).
  const surfaceGeometry: React.CSSProperties = isStandalone
    ? isMobile
      ? {
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          borderRadius: 0,
          // height / max-height come from CSS so 100dvh can fall back to 100vh
          // on browsers that don't know it.
          transition: "opacity 0.4s ease-in-out",
        }
      : {
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: constants.DIMENSIONS.STANDALONE.WIDTH_STRING,
          height: constants.DIMENSIONS.STANDALONE.HEIGHT_STRING,
          maxHeight: constants.DIMENSIONS.STANDALONE.HEIGHT_STRING,
          borderRadius: sliderState.isVisible ? "0px 28px 28px 0px" : "28px",
          transition:
            "opacity 0.4s ease-in-out, border-radius 0.4s ease-in-out",
        }
    : {
        position: isEditor ? "relative" : "fixed",
        [isLeft ? "left" : "right"]: isEditor
          ? "unset"
          : isMobile
          ? "0px"
          : `${panelAnchor.offset}px`,
        width: isMobile
          ? constants.DIMENSIONS.MOBILE.OPEN.WIDTH_STRING
          : constants.DIMENSIONS.DESKTOP.OPEN.WIDTH_STRING,
        bottom: isEditor
          ? "unset"
          : isMobile
          ? "0px"
          : `${panelAnchor.bottom}px`,
        height: openHeight,
        maxHeight: mountedOpen && isOpen ? openHeight : "0",
        // The slider docks on the inner side of the panel.
        borderRadius: isMobile
          ? "0px"
          : sliderState.isVisible
          ? isLeft
            ? "30px 0px 0px 30px"
            : "0px 30px 30px 0px"
          : "30px",
        transition:
          "max-height 0.4s ease-in-out, opacity 0.4s ease-in-out, border 0.4s ease-in-out",
      };

  // Growing out of the element the host site named. The panel is still
  // max-height:0 when this runs, so its box is derived from where it is about
  // to be rather than measured — a measured rect would be a zero-height sliver.
  const originGeometry: React.CSSProperties = (() => {
    if (!openOrigin || isStandalone || isEditor) return {};

    const width = isMobile
      ? window.innerWidth
      : constants.DIMENSIONS.DESKTOP.OPEN.WIDTH;
    const insetBottom = isMobile ? 0 : panelAnchor.bottom;
    const insetSide = isMobile ? 0 : panelAnchor.offset;
    const height = isMobile
      ? window.innerHeight
      : Math.min(
          constants.DIMENSIONS.DESKTOP.OPEN.HEIGHT,
          raisedPanel
            ? window.innerHeight - insetBottom - 20
            : constants.DIMENSIONS.DESKTOP.OPEN.HEIGHT
        );

    const left = isLeft ? insetSide : window.innerWidth - insetSide - width;
    const top = window.innerHeight - insetBottom - height;
    const clamp = (value: number, max: number) =>
      Math.min(Math.max(value, 0), max);

    return {
      transformOrigin: `${clamp(openOrigin.x - left, width)}px ${clamp(
        openOrigin.y - top,
        height
      )}px`,
      animation: "pl-origin-rise 640ms cubic-bezier(0.22, 1, 0.36, 1) both",
      // The max-height reveal would race the scale and read as two separate
      // openings; snap the box open and let the transform carry the motion.
      maxHeight: openHeight,
      transition: "opacity 0.2s ease-out",
    };
  })();

  // The real conversation (same state, same handlers) rendered as a native
  // column of the fullscreen surface. Only mounted while isImmersiveChat —
  // the floating widget's copy is unmounted, so refs and scroll stay sane.
  const immersiveChatPanel = isImmersiveChat ? (
    <div className="immersive-chat">
      {/* Same header as the widget (logo only), with the fullscreen close
          where the widget's minimize button lives. */}
      <div
        className="widget-header immersive-chat-head"
        style={{ backgroundColor: style.color, borderColor: style.color }}
      >
        <div style={{ display: "flex" }}>
          <img
            src={style.fileUrl}
            alt="Assistant Icon"
            className="header-icon inline-block"
          />
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          {/* Minimize, not close: fullscreen collapses back to the widget */}
          <button
            className="minimize-button immersive-minimize"
            onClick={() => sliderState.setExpanded(false)}
            aria-label={localization.get(language).minimize_view}
            title={localization.get(language).minimize_view}
          >
            <Minimize2 className="icon" aria-hidden="true" />
          </button>
        </div>
      </div>
      <ChatMessages
        messages={messages}
        style={style}
        isMobile={false}
        assistantChatConfiguration={assistantChatConfiguration}
        welcomeMessageTime={welcomeMessageTime}
        isMessageLoading={isMessageLoading}
        toolName={toolName}
        setFullScreen={setFullScreen}
        handleShortcutClick={handleShortcutClick}
        handleQuotePillClick={handleQuotePillClick}
        showHandoffButton={showHandoffButton}
        language={language}
        chatMessagesRef={attachChatMessages}
        lastMessageRef={attachLastMessage}
        assistantId={assistant.id}
        threadId={threadId}
      />
      <ChatFooter
        inputMessage={inputMessage}
        setInputMessage={setInputMessage}
        handleUserMessage={handleUserMessage}
        style={style}
        isEditor={isEditor}
        isMessageCompleted={isMessageCompleted}
        language={language}
        chatContainerRef={chatContainerRef}
        isMobile={false}
        assistantId={assistant.id}
        isVoiceMode={false}
        voiceModeEnabled={false}
        onVoiceToggle={() => {}}
        showAiDisclosure={!hasSentMessageThisSession}
      />
    </div>
  ) : undefined;

  return (
    style.loaded && (
      <section aria-label="Chat Widget">
        {/* In editor mode the launcher renders too (position: absolute inside
            the preview frame) so the backoffice can preview the closed state
            with teaser pills and badge. */}
        {(isEditor || ready) &&
          assistantChatConfiguration.loaded &&
          showsLauncher && (
            <ChatButton
              isOpen={isOpen}
              isEditor={isEditor}
              toggleChatWidget={toggleChatWidget}
              style={style}
              assistant={assistant}
              isMobile={isMobile}
              language={language}
              assistantChatConfiguration={assistantChatConfiguration}
              onTeaserClick={handleTeaserClick}
            />
          )}

        {/* Floating voice-call button — sonar rings, positioned to the left of chat button */}
        {!isEditor &&
          ready &&
          assistantChatConfiguration.loaded &&
          assistantChatConfiguration.isVoiceModeEnabled &&
          showsLauncher && (
            <button
              className="widget-voice-float-btn"
              style={{
                opacity: !isOpen ? "1" : "0",
                pointerEvents: !isOpen ? "auto" : "none",
                /* Same vertical level as the chat button, beside it on the
                   inner side */
                bottom: `${launcher.bottom}px`,
                [isLeft ? "left" : "right"]: `${
                  launcher.offset + (isMobile ? 70 : 92)
                }px`,
                width: isMobile ? "40px" : "46px",
                height: isMobile ? "40px" : "46px",
                backgroundColor: style.color,
                boxShadow: `0 4px 12px -2px ${style.color}88`,
              }}
              onClick={() => {
                setIsVoiceMode(true);
                if (!isOpen) toggleChatWidget("voice");
              }}
              aria-label="Start voice call"
              title="Start voice call"
            >
              {/* Same wave bars as footer mic button, white on brand color */}
              <span className="widget-mic-wave" style={{ color: "#fff" }}>
                <span />
                <span />
                <span />
                <span />
              </span>
            </button>
          )}

        <Slider
          isWidgetOpen={isOpen}
          isMobile={isMobile}
          style={style}
          language={language}
          chatPanel={immersiveChatPanel}
          isStandalone={isStandalone}
          panelAnchor={isEditor ? undefined : panelAnchor}
        />

        {isMobile && sliderState.isVisible && (
          <div
            style={{
              position: "fixed",
              top: "0",
              left: "0",
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: "999",
            }}
            onClick={sliderState.closeSlider}
          />
        )}

        <div
          ref={chatContainerRef}
          className={`plutonios-chat-surface${
            isStandalone ? " is-standalone" : ""
          }`}
          style={{
            ...surfaceGeometry,
            boxShadow: "0 14px 44px rgba(16, 11, 22, 0.24)",
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            // While the conversation lives inside the fullscreen surface the
            // floating widget disappears completely.
            opacity: mountedOpen && isOpen && !isImmersiveChat ? "1" : "0",
            pointerEvents: isImmersiveChat ? "none" : undefined,
            ...originGeometry,
          }}
        >
          {/**<div>
          <button
            onClick={() =>
              sliderState.openSlider(threadId, assistant.id, hotel_template, isMobile)
            }
          >
            hotel
          </button>
        </div> */}

          {fullScreen.isWIVFullScreen && (
            <WideImageViewer
              image={fullScreen.WIVfullScreenImage}
              maxHeight={
                isMobile
                  ? undefined
                  : constants.DIMENSIONS.DESKTOP.OPEN.HEIGHT_STRING
              }
              maxWidth={
                isMobile
                  ? undefined
                  : constants.DIMENSIONS.DESKTOP.OPEN.WIDTH_STRING
              }
              setFullScreen={(value: any) =>
                setFullScreen((prev) => ({ ...prev, isWIVFullScreen: value }))
              }
              isFullScreen={fullScreen.isWIVFullScreen}
              setFullScreenImage={(image: any) =>
                setFullScreen((prev) => ({
                  ...prev,
                  WIVfullScreenImage: image,
                }))
              }
            />
          )}

          {!isAnyFullScreen && !isImmersiveChat && (
            <>
              <ChatHeader
                style={style}
                isEditor={isEditor}
                toggleChatWidget={toggleChatWidget}
                cleanCache={cleanCache}
                language={language}
                assistant={assistant}
                canClose={!isStandalone}
              />
              {isVoiceMode ? (
                <VoicePanel
                  assistantId={assistant.id}
                  threadId={threadId}
                  isMobile={isMobile}
                  language={language}
                  style={style}
                  showHandoffButton={showHandoffButton}
                  assistantChatConfiguration={assistantChatConfiguration}
                  setFullScreen={setFullScreen}
                  onThreadIdChange={setThreadId}
                  onTurnComplete={(transcript, response, metadata) => {
                    Analytics.track("message_sent", {
                      source: "voice",
                      length: transcript?.length ?? 0,
                    });
                    const time = Utility.getTime();
                    const userMsg = { type: "user", content: transcript, time };
                    const botMsg = { type: "bot", content: response, time, metadata };
                    setMessages((prev) => [...prev, userMsg, botMsg]);
                    chatCache.addMessage(userMsg, threadId, assistant.id);
                    chatCache.addMessage(botMsg, threadId, assistant.id);
                  }}
                  onClose={() => setIsVoiceMode(false)}
                />
              ) : (
                <ChatMessages
                  messages={messages}
                  style={style}
                  isMobile={isMobile}
                  assistantChatConfiguration={assistantChatConfiguration}
                  welcomeMessageTime={welcomeMessageTime}
                  isMessageLoading={isMessageLoading}
                  toolName={toolName}
                  setFullScreen={setFullScreen}
                  handleShortcutClick={handleShortcutClick}
                  handleQuotePillClick={handleQuotePillClick}
                  showHandoffButton={showHandoffButton}
                  language={language}
                  chatMessagesRef={attachChatMessages}
                  lastMessageRef={attachLastMessage}
                  assistantId={assistant.id}
                  threadId={threadId}
                />
              )}
              <ChatFooter
                inputMessage={inputMessage}
                setInputMessage={setInputMessage}
                handleUserMessage={handleUserMessage}
                style={style}
                isEditor={isEditor}
                isMessageCompleted={isMessageCompleted}
                language={language}
                chatContainerRef={chatContainerRef}
                isMobile={isMobile}
                assistantId={assistant.id}
                isVoiceMode={isVoiceMode}
                voiceModeEnabled={assistantChatConfiguration.isVoiceModeEnabled ?? false}
                onVoiceToggle={() => setIsVoiceMode((v) => !v)}
                showAiDisclosure={!hasSentMessageThisSession}
              />
            </>
          )}

          <AnimatePresence>
            {fullScreen.isHandoffFormFullScreen && (
              <HandoffForm
                color={style.color}
                threadId={threadId}
                assistantId={assistant.id}
                setMessages={setMessages}
                setIsHandoffFormFullScreen={(value: any) =>
                  setFullScreen((prev) => ({
                    ...prev,
                    isHandoffFormFullScreen: value,
                  }))
                }
                setShowButton={setShowHandoffButton}
                language={language}
              />
            )}
          </AnimatePresence>
        </div>
      </section>
    )
  );
};
